import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { callStaffApi } from "@/lib/staffApi";
import BrandLogo from "@/components/feature/BrandLogo";
import Button from "@/components/base/Button";

interface ResolveLoginResponse {
  email: string;
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!username.trim() || !password) {
      setError("请输入用户名和密码");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const resolved = await callStaffApi<ResolveLoginResponse>({
        action: "resolve_login",
        username: username.trim().toLowerCase(),
      });
      if (!resolved.ok || !resolved.data?.email) {
        throw new Error(resolved.error || "用户名或密码不正确");
      }
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: resolved.data.email,
        password,
      });
      if (signInError) throw new Error("用户名或密码不正确");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error && err.message ? err.message : "登录失败，请稍后重试",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background-100 lg:grid-cols-2">
      <div className="relative hidden overflow-hidden lg:block">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20autumn%20golden%20ginkgo%20leaves%20soft%20bokeh%20background%20with%20a%20gentle%20silhouette%20of%20an%20elegant%20mature%20couple%20walking%20hand%20in%20hand%2C%20warm%20orange%20and%20amber%20tones%2C%20romantic%20serene%20atmosphere%2C%20high-end%20editorial%20photography%2C%20soft%20natural%20light%2C%20calm%20harmonious%20composition%20with%20negative%20space&width=900&height=1200&seq=admin-login-ginkgo&orientation=portrait"
          alt="锦伴金秋 运营后台"
          title="锦伴金秋 运营后台"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-foreground-950/60"></div>
        <div className="absolute inset-x-0 top-0 p-10">
          <BrandLogo onDark size="lg" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-10 text-background-50">
          <h2 className="font-heading text-2xl font-semibold leading-snug">
            用心服务每一段金秋良缘
          </h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-background-100/90">
            在这里查看客户提交的婚恋资料，筛选、跟进、撮合，让每一份真诚的期待都能被认真对待。
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-12 md:px-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden">
            <BrandLogo size="md" />
          </div>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 lg:mt-0">
            运营后台登录
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            请使用员工账号登录，查看与跟进客户资料
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-800" htmlFor="admin-username">
                用户名
              </label>
              <input
                id="admin-username"
                type="text"
                autoComplete="username"
                value={username}
                placeholder="请输入用户名"
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-300 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-800" htmlFor="admin-password">
                登录密码
              </label>
              <div className="relative">
                <input
                  id="admin-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  placeholder="请输入登录密码"
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 pr-11 text-sm text-foreground-900 placeholder:text-foreground-300 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-foreground-400 transition-colors hover:text-foreground-700"
                  aria-label={showPassword ? "隐藏密码" : "显示密码"}
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-800">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" variant="primary" size="lg" block disabled={loading}>
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  登录中...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line"></i>
                  登录后台
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 flex items-start gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-3">
            <i className="ri-information-line mt-0.5 text-secondary-500"></i>
            <p className="text-xs leading-relaxed text-foreground-500">
              员工账号由管理员在「员工管理」中创建（用户名 + 密码），不支持自助注册。若你是系统首位使用者，登录后可直接初始化成为管理员。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}